import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import BackLink from '../components/BackLink';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import CompletionRing from '../components/CompletionRing';
import FlameIcon from '../components/FlameIcon';
import { formatEventDate, parseDate, getDisplayStatus } from '../utils/date-utils';
import LogoSpinner from '../components/ui/LogoSpinner';
import { Alert } from '../components/ui/Alert';
import Reveal from '../components/ui/Reveal';
import { Appear } from '../components/ui/Appear';
import { Progress } from '../components/ui/Progress';
import TransportChips from '../components/TransportChips';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import { Field } from '../components/ui/Field';

// ─── Helpers ─────────────────────────────────────────────────

const getUserInitials = (user) => {
  if (!user) return '??';
  return ((user.firstname?.[0] || '') + (user.lastname?.[0] || '')).toUpperCase() || '??';
};

const formatCheckTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
};

const LONG_PRESS_MS = 500;

// ─── Long-Press Hook ─────────────────────────────────────────

const useLongPress = (onLongPress, onTap, { disabled = false } = {}) => {
  const timerRef = useRef(null);
  const didLongPress = useRef(false);
  const startPos = useRef(null);
  // Store callbacks in refs so the 500ms-delayed timer fires the LATEST
  // handler, not the one captured when the press started. Prevents
  // stale-closure bugs when component state changes mid-press.
  const onLongPressRef = useRef(onLongPress);
  const onTapRef = useRef(onTap);
  useEffect(() => {
    onLongPressRef.current = onLongPress;
    onTapRef.current = onTap;
  });

  const start = (e) => {
    if (disabled) return;
    didLongPress.current = false;
    const touch = e.touches?.[0];
    startPos.current = touch ? { x: touch.clientX, y: touch.clientY } : null;
    timerRef.current = setTimeout(() => {
      didLongPress.current = true;
      onLongPressRef.current();
    }, LONG_PRESS_MS);
  };

  const move = (e) => {
    if (!timerRef.current) return;
    const touch = e.touches?.[0];
    if (touch && startPos.current) {
      const dx = Math.abs(touch.clientX - startPos.current.x);
      const dy = Math.abs(touch.clientY - startPos.current.y);
      if (dx > 10 || dy > 10) { clearTimeout(timerRef.current); timerRef.current = null; }
    }
  };

  const end = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (!didLongPress.current && !disabled && onTapRef.current) onTapRef.current();
  };

  const cancel = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  return {
    onTouchStart: start, onTouchMove: move, onTouchEnd: end, onTouchCancel: cancel,
    onMouseDown: start, onMouseUp: end, onMouseLeave: cancel,
  };
};

// ─── Packing Item Row ────────────────────────────────────────

const PackingItem = ({ fullKey, item, check, onCheck, onDefer, onRemove, user, animDelay }) => {
  const isPacked = check?.status === 'packed';
  const isDeferred = check?.status === 'deferred';
  const isDone = isPacked || isDeferred;

  const [qty, setQty] = useState(check?.value_qty ?? '');
  const [soc, setSoc] = useState(check?.value_soc ?? '');
  const [text, setText] = useState(check?.value_text ?? '');
  const [saving, setSaving] = useState(false);
  const [pressing, setPressing] = useState(false);

  useEffect(() => {
    setQty(check?.value_qty ?? '');
    setSoc(check?.value_soc ?? '');
    setText(check?.value_text ?? '');
  }, [check?.value_qty, check?.value_soc, check?.value_text]);

  const handleTap = async () => {
    if (saving) return;
    setSaving(true);
    if (isPacked) {
      await onRemove(fullKey);
    } else if (isDeferred) {
      await onRemove(fullKey);
    } else {
      const values = {};
      if (item.type === 'qty' || item.type === 'qty_soc') values.qty = parseInt(qty) || null;
      if (item.type === 'soc' || item.type === 'qty_soc') values.soc = parseInt(soc) || null;
      if (item.type === 'text') values.text = text || null;
      if (item.type === 'initials') values.text = getUserInitials(user);
      await onCheck(fullKey, { ...values, status: 'packed' });
    }
    setSaving(false);
  };

  const handleLongPress = async () => {
    if (saving || isPacked) return;
    setPressing(false);
    setSaving(true);
    if (isDeferred) {
      await onRemove(fullKey);
    } else {
      await onDefer(fullKey);
    }
    setSaving(false);
  };

  const longPressProps = useLongPress(handleLongPress, handleTap, { disabled: saving });

  // Visual: start hold animation on touch, cancel on release
  const handlePressStart = (e) => {
    if (!saving && !isPacked) setPressing(true);
    longPressProps.onTouchStart?.(e);
  };
  const handlePressEnd = (e) => {
    setPressing(false);
    longPressProps.onTouchEnd?.(e);
  };
  const handlePressCancel = (e) => {
    setPressing(false);
    longPressProps.onTouchCancel?.(e);
  };
  const handleMouseDown = (e) => {
    if (!saving && !isPacked) setPressing(true);
    longPressProps.onMouseDown?.(e);
  };
  const handleMouseUp = (e) => {
    setPressing(false);
    longPressProps.onMouseUp?.(e);
  };
  const handleMouseLeave = (e) => {
    setPressing(false);
    longPressProps.onMouseLeave?.(e);
  };

  const checkerInitials = check ? ((check.firstname?.[0] || '') + (check.lastname?.[0] || '')).toUpperCase() : '';

  return (
    <div
      className={`pack-item relative flex items-center gap-3 px-3 py-3.5 rounded-lg transition-all duration-200 select-none
        ${isDone ? '' : 'active:scale-[0.98]'}
        ${isDeferred ? 'opacity-40' : ''}
        ${pressing && !isPacked ? 'bg-amber-500/5 scale-[0.97]' : 'hover:bg-gray-700/20'}`}
      style={{ animationDelay: `${animDelay}ms` }}
      onTouchStart={handlePressStart}
      onTouchEnd={handlePressEnd}
      onTouchCancel={handlePressCancel}
      onTouchMove={longPressProps.onTouchMove}
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {/* Long-press progress ring (behind checkbox) */}
      {pressing && !isPacked && (
        <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-8 h-8 pointer-events-none">
          <svg className="w-8 h-8 -rotate-90" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="14" fill="none" stroke="rgba(245,158,11,0.15)" strokeWidth="2" />
            <circle cx="16" cy="16" r="14" fill="none" stroke="rgba(245,158,11,0.6)" strokeWidth="2"
              strokeDasharray={`${2 * Math.PI * 14}`}
              strokeDashoffset={`${2 * Math.PI * 14}`}
              strokeLinecap="round"
              className="long-press-ring" />
          </svg>
        </div>
      )}

      {/* Checkbox */}
      <div className={`w-7 h-7 rounded-md border-2 flex-shrink-0 flex items-center justify-center transition-all duration-200
        ${isPacked
          ? 'bg-green-500/20 border-green-500 text-green-400 scale-100'
          : isDeferred
            ? 'bg-amber-500/10 border-amber-500/40 text-amber-400'
            : 'border-outline-variant bg-surface-container-high shadow-recess'
        } ${saving ? 'opacity-60' : ''}`}
      >
        {isPacked && (
          <svg className="w-4 h-4 pack-check-in" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        )}
        {isDeferred && (
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M18 12H6" />
          </svg>
        )}
      </div>

      {/* Label + inputs — single row layout */}
      <div className="flex-1 min-w-0 flex items-center gap-2">
        {/* Left: label + attribution */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[13px] font-medium leading-tight ${isDeferred ? 'line-through text-on-surface-variant' : 'text-on-surface'}`}>
              {item.label}
            </span>
            <Appear show={isDeferred}>
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-500/70 uppercase tracking-wider">
                Skipped
              </span>
            </Appear>
          </div>

          {/* Packed/deferred values + attribution */}
          <Reveal show={isDone}>
            <div className="flex items-center gap-2 mt-0.5 text-[10px] text-on-surface-variant">
              {check?.value_qty != null && <span className="text-on-surface-variant">Qty: {check.value_qty}</span>}
              {check?.value_soc != null && <span className="text-on-surface-variant">SOC: {check.value_soc}%</span>}
              {check?.value_text && <span className="text-on-surface-variant">{check.value_text}</span>}
              {checkerInitials && (
                <>
                  {(check?.value_qty != null || check?.value_soc != null || check?.value_text) && (
                    <span className="w-0.5 h-0.5 rounded-full bg-gray-700" />
                  )}
                  <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-700/60 text-[8px] font-bold text-on-surface-variant">
                    {checkerInitials}
                  </span>
                  <span>{formatCheckTime(check.checked_at)}</span>
                </>
              )}
            </div>
          </Reveal>
        </div>

        {/* Right: inline inputs (when unchecked and has fields) */}
        <Appear show={!isDone && (item.type === 'qty' || item.type === 'qty_soc' || item.type === 'soc' || item.type === 'text')} as="div" className="flex-shrink-0">
          <div className="flex items-center gap-1.5"
            onTouchStart={e => e.stopPropagation()} onTouchEnd={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()} onMouseUp={e => e.stopPropagation()}>
            {(item.type === 'qty' || item.type === 'qty_soc') && (
              <div className="inline-flex items-center gap-1">
                <span className="text-[10px] text-on-surface-variant">QTY</span>
                <Field size="lg" type="number" inputMode="numeric" value={qty} onChange={e => setQty(e.target.value)}
                  className="!w-11 !px-1 text-center" />
              </div>
            )}
            {(item.type === 'soc' || item.type === 'qty_soc') && (
              <div className="inline-flex items-center gap-1">
                <span className="text-[10px] text-on-surface-variant">SOC</span>
                <Field size="lg" type="number" inputMode="numeric" min="0" max="100" value={soc} onChange={e => setSoc(e.target.value)}
                  className="!w-11 !px-1 text-center" />
                <span className="text-[10px] text-on-surface-variant">%</span>
              </div>
            )}
            {item.type === 'text' && (
              <Field size="lg" type="text" value={text} onChange={e => setText(e.target.value)}
                placeholder={item.placeholder || ''}
                className="!w-28" />
            )}
          </div>
        </Appear>
      </div>
    </div>
  );
};

// ─── Category Card ───────────────────────────────────────────

const CategoryCard = ({ category, checks, onCheck, onDefer, onRemove, user, isCompleteness, index }) => {
  const filled = category.items.filter(item => checks.has(`${category.category}.${item.key}`)).length;
  const total = category.items.length;
  const complete = filled === total;

  return (
    <div
      className={`rounded-xl border transition-all duration-300 overflow-hidden pack-card-in
        ${complete
          ? 'bg-green-500/[0.03] border-green-500/20'
          : isCompleteness
            ? 'bg-amber-500/[0.02] border-amber-500/20'
            : 'bg-surface-container-low border-transparent'}`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Section heading */}
      <div className={`flex items-center gap-2.5 px-4 py-3 border-b
        ${complete ? 'border-green-500/10' : isCompleteness ? 'border-amber-500/10' : 'border-gray-700/30'}`}>
        <div className={`w-1 h-4 rounded-full transition-colors duration-500
          ${complete ? 'bg-green-500/70' : isCompleteness ? 'bg-amber-500/50' : 'bg-primary/50'}`} />
        <h3 className={`text-[11px] uppercase tracking-[0.12em] font-semibold flex-1 transition-colors duration-300
          ${complete ? 'text-green-500/70' : 'text-on-surface-variant'}`}>
          {category.label}
        </h3>
        <CompletionRing filled={filled} total={total} size={20} />
        <span className={`text-[11px] tabular-nums font-medium transition-colors duration-300
          ${complete ? 'text-green-500/70' : 'text-on-surface-variant'}`}>
          {filled}/{total}
        </span>
        {complete && (
          <svg className="w-4 h-4 text-green-500/60 pack-check-in" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        )}
      </div>

      {/* Items */}
      <div className="px-1 py-1">
        {category.items.map((item, i) => {
          const fullKey = `${category.category}.${item.key}`;
          return (
            <PackingItem
              key={fullKey}
              fullKey={fullKey}
              item={item}
              check={checks.get(fullKey)}
              onCheck={onCheck}
              onDefer={onDefer}
              onRemove={onRemove}
              user={user}
              animDelay={index * 60 + i * 20}
            />
          );
        })}
      </div>
    </div>
  );
};

// ─── Ship Form (sticky bottom) ──────────────────────────────

const ShipForm = ({ event, filled, total, onShip }) => {
  const [tracking, setTracking] = useState('');
  const [shipping, setShipping] = useState(false);
  const allDone = filled >= total;
  const isShipping = event?.transport_mode === 'shipping';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allDone || shipping) return;
    setShipping(true);
    await onShip(isShipping ? tracking.trim() : null);
    setShipping(false);
  };

  return (
    <div className="h-full">
      <form onSubmit={handleSubmit}
        className="bg-surface-container-low border border-transparent rounded-xl p-4 space-y-3 h-full flex flex-col justify-between">

        {/* Progress bar */}
        <Progress value={filled} max={total} label="Items packed" display="count" success size="lg" />

        {/* Tracking input (shipping mode only) */}
        {isShipping && (
          <Field
            size="lg"
            type="text"
            value={tracking}
            onChange={e => setTracking(e.target.value)}
            placeholder="Tracking number (optional)"
          />
        )}

        {/* Ship button */}
        <button
          type="submit"
          disabled={!allDone || shipping}
          className={`${buttonClass('filled', 'lg')} w-full`}
        >
          {shipping
            ? 'Updating...'
            : allDone
              ? (isShipping ? 'Mark Shipped' : 'Mark Sent')
              : `${total - filled} item${total - filled !== 1 ? 's' : ''} remaining`}
        </button>
      </form>
    </div>
  );
};

// ─── Main Page ───────────────────────────────────────────────

const PackingPage = () => {
  const { id } = useParams();
  const history = useHistory();
  const api = useApi();
  const toast = useToast();
  const user = useUserStore(state => state.user);

  const [template, setTemplate] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [checks, setChecks] = useState(new Map());
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const data = await api.get(`/events/${id}/packing`, { silent: true });
      setTemplate(data.template);
      setTotalItems(data.totalItems);
      setEvent(data.event);
      const map = new Map();
      (data.checks || []).forEach(c => map.set(c.item_key, c));
      setChecks(map);
    } catch {
      toast.error('Failed to load packing data');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filledCount = checks.size;
  const pct = totalItems > 0 ? Math.round((filledCount / totalItems) * 100) : 0;
  const canPack = ['confirmed', 'packing'].includes(event?.status) && (event?.tour?.allConfirmed !== false);

  const handleCheck = async (itemKey, values) => {
    const prevValue = checks.get(itemKey);
    setChecks(m => {
      const next = new Map(m);
      next.set(itemKey, {
        item_key: itemKey, status: values.status || 'packed',
        checked_by: user?.id, firstname: user?.firstname, lastname: user?.lastname,
        checked_at: new Date().toISOString(),
        value_qty: values.qty ?? null, value_text: values.text ?? null, value_soc: values.soc ?? null,
      });
      return next;
    });
    try {
      const row = await api.put(`/events/${id}/packing/${itemKey}`, { body: values, silent: true });
      setChecks(m => { const next = new Map(m); next.set(itemKey, row); return next; });
      if (row._statusChanged) setEvent(e => ({ ...e, status: row._statusChanged }));
    } catch {
      setChecks(m => { const next = new Map(m); prevValue ? next.set(itemKey, prevValue) : next.delete(itemKey); return next; });
      toast.error('Failed to save');
    }
  };

  const handleDefer = async (itemKey) => {
    const prevValue = checks.get(itemKey);
    setChecks(m => {
      const next = new Map(m);
      next.set(itemKey, {
        item_key: itemKey, status: 'deferred',
        checked_by: user?.id, firstname: user?.firstname, lastname: user?.lastname,
        checked_at: new Date().toISOString(),
      });
      return next;
    });
    try {
      const row = await api.put(`/events/${id}/packing/${itemKey}`, { body: { status: 'deferred' }, silent: true });
      setChecks(m => { const next = new Map(m); next.set(itemKey, row); return next; });
    } catch {
      setChecks(m => { const next = new Map(m); prevValue ? next.set(itemKey, prevValue) : next.delete(itemKey); return next; });
      toast.error('Failed to save');
    }
  };

  const handleRemove = async (itemKey) => {
    const prevValue = checks.get(itemKey);
    setChecks(m => { const next = new Map(m); next.delete(itemKey); return next; });
    try {
      const res = await api.delete(`/events/${id}/packing/${itemKey}`, { silent: true });
      if (res?._statusChanged) setEvent(e => ({ ...e, status: res._statusChanged }));
    } catch {
      setChecks(m => { const next = new Map(m); if (prevValue) next.set(itemKey, prevValue); return next; });
      toast.error('Failed to save');
    }
  };

  const handleShip = async (trackingNumber) => {
    try {
      const body = {};
      if (trackingNumber) body.tracking_number = trackingNumber;
      await api.post(`/events/${id}/packing/ship`, { body, silent: true });
      toast.success(event?.transport_mode === 'shipping' ? 'Gear shipped!' : 'Gear sent!');
      history.push(`/events/${id}`);
    } catch {
      toast.error('Failed to ship — are all items checked?');
    }
  };

  if (loading) {
    return <LogoSpinner label="Loading the packing list" />;
  }

  if (!event) {
    return (
      <PageLayout kind="focus" className="pt-10 text-center">
        <p className="text-on-surface-variant">Event not found</p>
        <BackLink to="/dispatch" label="Dispatch" className="mt-2" />
      </PageLayout>
    );
  }

  const pyroCount = event.tour ? event.tour.maxPyro : event.buffered_pyro;

  return (
    <PageLayout kind="record" className="pt-5">
      {/* Pyro warning banner — flame static (animated={false}) so it prints cleanly */}
      {pyroCount > 0 && (
        <Alert
          tone="warning"
          live={false}
          className="mb-4"
          title="This event has pyro drones"
          icon={<FlameIcon className="w-7 h-7 flex-shrink-0" animated={false} title="Pyro" />}
        >
          {pyroCount} pyro {pyroCount === 1 ? 'drone' : 'drones'} in this load. Handle and pack accordingly.
        </Alert>
      )}

      {/* Header row — sticky on mobile, static on desktop */}
      <div className="sticky top-0 z-20 lg:static flex flex-col lg:flex-row gap-4 mb-5 pack-card-in
                       -mx-4 px-4 sm:-mx-6 sm:px-6 pt-1 pb-4 lg:mx-0 lg:px-0 lg:pt-0 lg:pb-0">
        {/* Info card */}
        <div className="flex-1 bg-surface-container-low border border-transparent rounded-xl p-4 min-w-0 shadow-lg shadow-gray-900/50 lg:shadow-none">
          <div className="flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <BackLink to="/dispatch" label="Dispatch" className="mb-2" />
              <h1 className="text-base font-semibold text-white truncate leading-tight">{event.name}</h1>
              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                <span className="text-[11px] text-on-surface-variant">{formatEventDate(event.event_date)}</span>
                {event.end_date && event.end_date !== event.event_date && (
                  <span className="text-[11px] text-on-surface-variant">– {formatEventDate(event.end_date, 'MMM D')}</span>
                )}
                <span className="w-1 h-1 rounded-full bg-gray-700" />
                {/* Both facts: a truck on the show means we drive, and the mode
                    describes the leg somebody else carries. The ship form below
                    still keys its tracking-number field off the mode alone, since
                    freight is what has a tracking number. */}
                <TransportChips
                  hasVehicle={!!event.has_vehicle}
                  transportArranged={!!event.transport_arranged}
                  transportMode={event.transport_mode}
                  size="compact"
                />
                {event.tour && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-gray-700" />
                    <Link to={`/events/tours/${event.tour.id}`} className="text-[11px] text-cyan-400 hover:text-cyan-300 transition-colors">
                      {event.tour.name} ({event.tour.stops} stops)
                    </Link>
                  </>
                )}
              </div>
            </div>
            {/* Progress ring */}
            <div className="relative flex-shrink-0">
              <CompletionRing filled={filledCount} total={totalItems} size={44} />
              <span className="absolute inset-0 flex items-center justify-center text-[11px] font-bold tabular-nums text-on-surface">
                {pct}%
              </span>
            </div>
          </div>

          {/* Event details strip */}
          <div className="flex items-center gap-2.5 mt-3 pt-3 border-t border-gray-700/30 flex-wrap text-[11px]">
            <span className="text-on-surface-variant italic">Buffered:</span>
            <span className="text-on-surface-variant font-medium">
              {event.tour ? event.tour.maxDrones : event.buffered_total} drones
            </span>
            {(event.tour ? event.tour.maxPyro : event.buffered_pyro) > 0 && (
              <span className="text-purple-400 font-medium">({event.tour ? event.tour.maxPyro : event.buffered_pyro} pyro)</span>
            )}
            <span className="w-1 h-1 rounded-full bg-gray-700" />
            <span className="text-on-surface-variant font-medium">
              {event.batteries} batteries
            </span>
            {event.is_double_show && (
              <>
                <span className="w-1 h-1 rounded-full bg-gray-700" />
                <span className="text-amber-400/70 font-medium">Double show</span>
              </>
            )}
            {(event.tour?.surfaceTypes?.length > 0 || event.surface_type) && (
              <>
                <span className="w-1 h-1 rounded-full bg-gray-700" />
                <span className="text-on-surface-variant">
                  {event.tour?.surfaceTypes?.length > 0
                    ? event.tour.surfaceTypes.join(', ')
                    : event.surface_type}
                </span>
              </>
            )}
            <span className="w-1 h-1 rounded-full bg-gray-700" />
            <span className="text-on-surface-variant">
              Out {formatEventDate(event.tour?.shipOut || event.ship_out_date, 'M/D')} · Back {formatEventDate(event.tour?.shipBack || event.ship_back_date, 'M/D')}
            </span>
          </div>

          {/* Packing notes — all tour stops if tour, otherwise single event */}
          {event.tour?.packingNotes?.length > 0 ? (
            <div className="mt-2.5 pt-2.5 border-t border-gray-700/30 space-y-2">
              {event.tour.packingNotes.map((stop, i) => (
                <div key={i}>
                  <span className="text-[10px] text-on-surface-variant font-medium">
                    {stop.name} ({formatEventDate(stop.event_date, 'M/D')}):
                  </span>
                  <div className="text-[11px] text-on-surface-variant leading-relaxed mt-0.5 pl-2 border-l border-gray-700/30 [&>div]:min-h-[1em]"
                    dangerouslySetInnerHTML={{ __html: stop.notes }} />
                </div>
              ))}
            </div>
          ) : event.packing_notes ? (
            <div className="mt-2.5 pt-2.5 border-t border-gray-700/30">
              <div className="text-[11px] text-on-surface-variant leading-relaxed [&>div]:min-h-[1em]"
                dangerouslySetInnerHTML={{ __html: event.packing_notes }} />
            </div>
          ) : null}

          {/* Progress bar — mobile only (desktop has it in the ship form) */}
          <div className="mt-3 lg:hidden">
            <Progress value={filledCount} max={totalItems} label="Items packed" display="count" success="Ready" size="md" />
          </div>

          {/* Hint */}
          <p className="text-[10px] text-on-surface-variant mt-2 tracking-wide">
            Tap to check · Long-press to skip
          </p>
        </div>

        {/* Ship form — desktop only (beside header) */}
        {(event.status === 'confirmed' || event.status === 'packing') ? (
          <div className="hidden lg:block lg:w-72 flex-shrink-0">
            <ShipForm event={event} filled={filledCount} total={totalItems} onShip={handleShip} />
          </div>
        ) : (
          <div className="hidden lg:flex lg:w-72 flex-shrink-0 items-center justify-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/5 border border-green-500/20">
              <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-green-400/80 font-medium">
                {getDisplayStatus(event) === 'completed'
                  ? 'Completed'
                  : getDisplayStatus(event) === 'active'
                    ? 'Active — gear is in transit'
                    : getDisplayStatus(event).charAt(0).toUpperCase() + getDisplayStatus(event).slice(1)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Tour not fully confirmed banner */}
      {event.tour && !event.tour.allConfirmed && (
        <Alert tone="warning" live={false} className="mb-4" title="Not all tour stops are confirmed">
          {event.tour.events.filter(e => !['confirmed', 'packing', 'active'].includes(e.status)).map(e => e.name).join(', ')} still pending. Confirm all stops to begin packing.
        </Alert>
      )}

      {/* Category cards + ship form — CSS columns for masonry flow on desktop */}
      <div className={`pack-columns ${!canPack ? 'opacity-40 pointer-events-none' : ''}`}>
        {template.map((cat, i) => (
          <div key={cat.category} className="mb-3 break-inside-avoid">
            <CategoryCard
              category={cat}
              checks={checks}
              onCheck={handleCheck}
              onDefer={handleDefer}
              onRemove={handleRemove}
              user={user}
              isCompleteness={cat.category === 'completeness'}
              index={i}
            />
          </div>
        ))}

      </div>

      {/* Ship form — mobile only (at the bottom after checklist) */}
      {(event.status === 'confirmed' || event.status === 'packing') ? (
        <div className="lg:hidden">
          <ShipForm event={event} filled={filledCount} total={totalItems} onShip={handleShip} />
        </div>
      ) : (
        <div className="lg:hidden mt-4 mb-4 flex justify-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/5 border border-green-500/20">
            <svg className="w-4 h-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm text-green-400/80 font-medium">
              {event.status === 'active' && parseDate(event.end_date || event.event_date).isBefore(parseDate(new Date().toISOString().slice(0, 10)))
                ? 'Completed'
                : event.status === 'active'
                  ? 'Active — gear is in transit'
                  : event.status.charAt(0).toUpperCase() + event.status.slice(1)}
            </span>
          </div>
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes packCardIn {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .pack-card-in {
          animation: packCardIn 0.4s ease-out both;
        }
        @keyframes packCheckIn {
          0% { transform: scale(0) rotate(-20deg); opacity: 0; }
          60% { transform: scale(1.2) rotate(0deg); }
          100% { transform: scale(1) rotate(0deg); opacity: 1; }
        }
        .pack-check-in {
          animation: packCheckIn 0.3s ease-out;
        }
        @keyframes longPressRing {
          from { stroke-dashoffset: ${2 * Math.PI * 14}; }
          to { stroke-dashoffset: 0; }
        }
        .long-press-ring {
          animation: longPressRing ${LONG_PRESS_MS}ms linear forwards;
        }
        @media (min-width: 1024px) {
          .pack-columns {
            columns: 3;
            column-gap: 0.75rem;
          }
        }
      `}</style>
    </PageLayout>
  );
};

export default PackingPage;
